import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Modal } from "@opendate-app/setlist";
import "./classesShared.css";

export default function DecisionModal({
  type = "brand",
  icon,
  title,
  description,
  children,
  cancelLabel = "Cancel",
  confirmLabel,
  onConfirm,
  onClose,
  working = false,
  confirmDisabled = false,
}) {
  const bodyRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (working) return undefined;
    const closeUnlessPopupOpen = (event) => {
      if (event.key !== "Escape") return;
      if (bodyRef.current && bodyRef.current.querySelector('[aria-expanded="true"]')) return;
      event.stopPropagation();
      onCloseRef.current();
    };
    window.addEventListener("keydown", closeUnlessPopupOpen, true);
    return () => window.removeEventListener("keydown", closeUnlessPopupOpen, true);
  }, [working]);

  const footer = (
    <div className="classes-decision-modal-actions">
      <span className="sr-only" role="status">{working ? confirmLabel : ""}</span>
      <Button variant="secondary" onClick={working ? undefined : onClose} aria-disabled={working}>{cancelLabel}</Button>
      <Button
        variant={type === "error" ? "destructive" : "primary"}
        onClick={working ? undefined : onConfirm}
        aria-disabled={working}
        aria-busy={working}
        disabled={confirmDisabled}
      >
        {confirmLabel}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      layout="vertical"
      width={480}
      type={type}
      icon={icon || true}
      showClose={false}
      closeOnBackdrop={!working}
      closeOnEsc={false}
      title={title}
      description={description}
      footer={footer}
    >
      {children && <div ref={bodyRef}>{children}</div>}
    </Modal>
  );
}

DecisionModal.propTypes = {
  type: PropTypes.oneOf(["brand", "warning", "error", "success"]),
  icon: PropTypes.elementType,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  children: PropTypes.node,
  cancelLabel: PropTypes.node,
  confirmLabel: PropTypes.node.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  working: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
};
